import { useState } from 'react';
import type { ApiEndpoints } from '@avisoma/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import backend from '../../services/backend';
import { Card } from '../../components/Card';
import { Input } from '../../catalyst/input';
import { Textarea } from '../../catalyst/textarea';
import { Button } from '../../catalyst/button';
import LabelEditor from '../../components/LabelEditor';
import Tag from '../../components/Tag';
import AuthGuard from '../../components/AuthGuard';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { formatDateTime } from '../../lib/dateFormat';
import { readCategoryGuardError } from '../../lib/categoryGuardError';
import type { CategoryResponse } from './types';

export function CategoryDetails({ response }: { response: CategoryResponse }) {
  const clientInformation = useQueryClient();
  const [name, setName] = useState(response.categoryDetails.name);
  // Empty string is the editor's representation of "no description"; the server maps it
  // back to NULL, so the two never have to be distinguished in the field itself.
  const [description, setDescription] = useState(response.categoryDetails.description ?? '');
  const [embeddingClustersThreshold, setEmbeddingClustersThreshold] = useState(
    response.categoryDetails.embeddingClustersThreshold,
  );

  const saveCategoryMutation = useMutation({
    mutationFn: (updates: ApiEndpoints<string>['/api/categories/:id']['PATCH']['body']) =>
      backend.patchCategory(response.categoryDetails.id, updates),
    onSuccess: (res, variables) => {
      // The PATCH response is a bare category row — spread it over the cached details
      // instead of replacing them, or GET-only fields (linkedPageCount, pageIds) go blank
      // until the next refetch.
      clientInformation.setQueryData(['getCategory', res.data.id.toString()], (old: CategoryResponse) => ({
        ...old,
        categoryDetails: { ...old.categoryDetails, ...res.data },
      }));
      // The list page reads from its own cache and now shows the description column.
      void clientInformation.invalidateQueries({ queryKey: ['categories'] });
      // The server trims and maps whitespace-only to NULL, so what it stored can differ
      // from what is in the box. Adopt the stored value — but only when this save carried
      // the description and the box still holds what was submitted; anything typed while
      // the PATCH was in flight wins over the older value the server echoes back.
      if (variables.description !== undefined) {
        setDescription((current) =>
          current.trim() === variables.description ? (res.data.description ?? '') : current,
        );
      }
    },
  });

  // The server stores trimmed text, so the draft has to be compared and sent trimmed too —
  // otherwise trailing whitespace reads as an edit the save can never settle.
  const trimmedDescription = description.trim();
  const descriptionDirty = trimmedDescription !== (response.categoryDetails.description ?? '');

  const handleSave = () => {
    const updates: ApiEndpoints<string>['/api/categories/:id']['PATCH']['body'] = {};

    if (name !== response.categoryDetails.name) {
      updates.name = name;
    }

    if (descriptionDirty) {
      updates.description = trimmedDescription;
    }

    if (embeddingClustersThreshold !== response.categoryDetails.embeddingClustersThreshold) {
      updates.embeddingClustersThreshold = embeddingClustersThreshold;
    }

    saveCategoryMutation.mutate(updates);
  };

  const saving = saveCategoryMutation.isPending;

  // A rename can be refused (409) when the category's name is referenced in extraction
  // code or in a supertype spec. Without this the request failed silently apart from the
  // generic axios toast, and the field kept showing the name the user typed as if saved.
  // Scoped to renames via the mutation's own variables so a threshold-only failure does
  // not surface under the name field.
  const renameFailure =
    saveCategoryMutation.error && saveCategoryMutation.variables?.name !== undefined
      ? readCategoryGuardError(saveCategoryMutation.error)
      : null;

  return (
    <Card header={<h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Category Information</h2>}>
      <DescriptionList>
        <DescriptionTerm>Category</DescriptionTerm>
        <DescriptionDetails>
          <AuthGuard roles={['admin']} fallback={<div>{response.categoryDetails.name}</div>}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
                {name !== response.categoryDetails.name && (
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
              {renameFailure && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                  {renameFailure.message}
                </div>
              )}
            </div>
          </AuthGuard>
        </DescriptionDetails>
        <DescriptionTerm>Description</DescriptionTerm>
        <DescriptionDetails>
          <AuthGuard
            roles={['admin']}
            fallback={<div className="whitespace-pre-wrap">{response.categoryDetails.description ?? '—'}</div>}
          >
            <div className="flex flex-col items-start gap-2">
              <Textarea
                rows={4}
                value={description}
                placeholder="What belongs in this category, and how it differs from neighbouring ones."
                onChange={(e) => setDescription(e.target.value)}
              />
              {descriptionDirty && (
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </AuthGuard>
        </DescriptionDetails>
        <DescriptionTerm>Input Count</DescriptionTerm>
        <DescriptionDetails>{response.categoryDetails.inputCount}</DescriptionDetails>
        <DescriptionTerm>Embedding Clusters Count</DescriptionTerm>
        <DescriptionDetails>{response.categoryDetails.embeddingClustersCount}</DescriptionDetails>
        <DescriptionTerm>Embedding Clusters Threshold</DescriptionTerm>
        <DescriptionDetails>
          <AuthGuard roles={['admin']} fallback={<div>{response.categoryDetails.embeddingClustersThreshold}</div>}>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={embeddingClustersThreshold}
                step={0.05}
                min={0}
                max={1}
                onChange={(e) => {
                  setEmbeddingClustersThreshold(Number(e.target.value));
                }}
              />
              {embeddingClustersThreshold !== response.categoryDetails.embeddingClustersThreshold && (
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </AuthGuard>
        </DescriptionDetails>
        <DescriptionTerm>Linked Page Count</DescriptionTerm>
        <DescriptionDetails>{response.categoryDetails.linkedPageCount}</DescriptionDetails>
        <DescriptionTerm>Excluded</DescriptionTerm>
        <DescriptionDetails>{response.categoryDetails.excluded ? 'YES' : 'NO'}</DescriptionDetails>
        <DescriptionTerm>Created At</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(response.categoryDetails.createdAt)}</DescriptionDetails>
        <DescriptionTerm>Updated At</DescriptionTerm>
        <DescriptionDetails>{formatDateTime(response.categoryDetails.updatedAt)}</DescriptionDetails>
        <DescriptionTerm>Release Certificate Description Tags</DescriptionTerm>
        <DescriptionDetails>
          <AuthGuard
            roles={['admin', 'supervisor']}
            fallback={
              <div className="max-h-48 overflow-auto flex flex-wrap gap-1">
                {response.releaseCertificateEmbeddings.map((embedding) => (
                  <Tag key={embedding.id} tag={embedding.text} />
                ))}
              </div>
            }
          >
            <div className="max-h-48 overflow-auto">
              <LabelEditor
                items={response.releaseCertificateEmbeddings.map((embedding) => embedding.text)}
                onChange={async (event) => {
                  if (event.action === 'add') {
                    const inserted = await backend.postCategoryRCEmbeddings(event.item, response.categoryDetails.id);

                    clientInformation.setQueryData(
                      ['getCategory', response.categoryDetails.id.toString()],
                      (old: CategoryResponse) => ({
                        ...old,
                        releaseCertificateEmbeddings: [...old.releaseCertificateEmbeddings, inserted.data],
                      }),
                    );
                  }

                  if (event.action === 'remove') {
                    const item = response.releaseCertificateEmbeddings.find(
                      (embedding) => embedding.text === event.item,
                    );

                    if (item) {
                      await backend.deleteCategoryRCEmbeddings(item.id);

                      clientInformation.setQueryData(
                        ['getCategory', response.categoryDetails.id.toString()],
                        (old: CategoryResponse) => ({
                          ...old,
                          releaseCertificateEmbeddings: old.releaseCertificateEmbeddings.filter(
                            (embedding) => embedding.id !== item.id,
                          ),
                        }),
                      );
                    }
                  }
                }}
              />
            </div>
          </AuthGuard>
        </DescriptionDetails>
      </DescriptionList>
    </Card>
  );
}
